import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { Check, ChevronLeft, MoreVertical, X } from "lucide-react";
import type { ReactNode } from "react";

export interface TopAppBarMenuOption {
  label: string;
  onClick: () => void;
}

type TopAppBarBase = {
  variant: "back" | "close";
  onBack?: () => void;
  onClose?: () => void;
  title?: string;
  centerContent?: ReactNode;
};

type TopAppBarRightSave = { onSave: () => void; menuOptions?: never };
type TopAppBarRightMenu = { menuOptions: TopAppBarMenuOption[]; onSave?: never };
type TopAppBarRightNone = { onSave?: never; menuOptions?: never };

export type TopAppBarProps = TopAppBarBase & (TopAppBarRightSave | TopAppBarRightMenu | TopAppBarRightNone);

const BUTTON_SIDE_CLASS =
  "flex h-10 w-10 shrink-0 items-center justify-center rounded-full hover:bg-gray-200 transition-colors";

/**
 * Fixed top app bar with left action (back/close), centre (title or custom content),
 * and optional right (save tick or menu).
 *
 * Only one of onSave or menuOptions may be provided.
 *
 * Note: This component is fixed positioned. Parent layouts should add pt-14 to content below.
 */
export default function TopAppBar({
  variant,
  onBack,
  onClose,
  title,
  centerContent,
  onSave,
  menuOptions,
}: TopAppBarProps) {
  const leftAction =
    variant === "back" ? (
      onBack != null ? (
        <button
          type="button"
          onClick={onBack}
          className={`${BUTTON_SIDE_CLASS} -ml-2 p-2`}
          aria-label="Back"
          data-testid="top-app-bar-back-button"
        >
          <ChevronLeft className="h-6 w-6 text-gray-800" />
        </button>
      ) : null
    ) : onClose != null ? (
      <button
        type="button"
        onClick={onClose}
        className={`${BUTTON_SIDE_CLASS} -ml-2 p-2`}
        aria-label="Close"
        data-testid="top-app-bar-close-button"
      >
        <X className="h-6 w-6 text-gray-800" />
      </button>
    ) : null;

  const center =
    centerContent != null ? (
      centerContent
    ) : title != null ? (
      <span className="font-mono font-extrabold text-xl text-gray-900 truncate" data-testid="top-app-bar-title">
        {title}
      </span>
    ) : null;

  const rightSlot =
    onSave != null ? (
      <button
        type="button"
        onClick={onSave}
        className={BUTTON_SIDE_CLASS}
        aria-label="Save"
        data-testid="top-app-bar-save-button"
      >
        <Check className="h-6 w-6 text-gray-800" />
      </button>
    ) : menuOptions != null ? (
      <Menu as="div" className="relative shrink-0">
        <MenuButton
          type="button"
          className={BUTTON_SIDE_CLASS}
          aria-label="More options"
          data-testid="top-app-bar-menu-button"
        >
          <MoreVertical className="h-6 w-6 text-gray-800" />
        </MenuButton>
        <MenuItems
          anchor="bottom end"
          className="mt-2 min-w-48 origin-top-right rounded-lg border border-gray-200 bg-white shadow-lg ring-1 ring-black/5 focus:outline-none z-10"
        >
          <div className="p-1">
            {menuOptions.map((opt) => (
              <MenuItem key={opt.label}>
                <button
                  type="button"
                  onClick={opt.onClick}
                  className="flex w-full px-3 py-2 text-left text-sm text-gray-900 rounded-md data-[focus]:bg-gray-100 transition-colors"
                >
                  {opt.label}
                </button>
              </MenuItem>
            ))}
          </div>
        </MenuItems>
      </Menu>
    ) : (
      <div className="h-10 w-10 shrink-0" aria-hidden />
    );

  return (
    <header
      className="fixed inset-x-0 top-0 z-10 flex h-14 items-center justify-between px-4 py-3 bg-gray-100 border-b border-gray-200"
      data-testid="top-app-bar"
    >
      <div className="flex h-10 w-10 shrink-0 items-center justify-center">{leftAction}</div>
      <div className="flex min-w-0 flex-1 justify-center px-2">{center}</div>
      <div className="flex h-10 w-10 shrink-0 items-center justify-center">{rightSlot}</div>
    </header>
  );
}
